/**
 * Monotone cubic interpolation for the tone curves.
 *
 * Catmull-Rom overshoots, which on a tone curve shows up as a visible reversal
 * — the image gets *darker* as you drag a point up. The Fritsch–Carlson
 * monotone variant cannot overshoot, so dragging a point always moves the
 * image the way the photographer expects.
 */

import type { CurvePoint } from './recipe.ts';

export const CURVE_LUT_SIZE = 256;

/**
 * Builds a lookup table of `size` samples over the 0..1 input domain.
 * Returned values are clamped to 0..1.
 */
export function buildCurveLut(points: CurvePoint[], size = CURVE_LUT_SIZE): Float32Array {
  const lut = new Float32Array(size);
  const sorted = [...points].sort((a, b) => a.x - b.x);

  if (sorted.length < 2) {
    for (let i = 0; i < size; i += 1) lut[i] = i / (size - 1);
    return lut;
  }

  const n = sorted.length;
  const xs = sorted.map((p) => p.x);
  const ys = sorted.map((p) => p.y);

  // Secant slopes between consecutive points.
  const secants = new Array<number>(n - 1);
  for (let i = 0; i < n - 1; i += 1) {
    const dx = xs[i + 1] - xs[i];
    secants[i] = dx > 1e-9 ? (ys[i + 1] - ys[i]) / dx : 0;
  }

  // Initial tangents: average of neighbouring secants.
  const tangents = new Array<number>(n);
  tangents[0] = secants[0];
  tangents[n - 1] = secants[n - 2];
  for (let i = 1; i < n - 1; i += 1) {
    tangents[i] = (secants[i - 1] + secants[i]) / 2;
  }

  // Fritsch–Carlson correction: clamp tangents so the curve stays monotone.
  for (let i = 0; i < n - 1; i += 1) {
    if (secants[i] === 0) {
      tangents[i] = 0;
      tangents[i + 1] = 0;
      continue;
    }
    const alpha = tangents[i] / secants[i];
    const beta = tangents[i + 1] / secants[i];
    const magnitude = alpha * alpha + beta * beta;
    if (magnitude > 9) {
      const scale = 3 / Math.sqrt(magnitude);
      tangents[i] = scale * alpha * secants[i];
      tangents[i + 1] = scale * beta * secants[i];
    }
  }

  let segment = 0;
  for (let i = 0; i < size; i += 1) {
    const x = i / (size - 1);
    while (segment < n - 2 && x > xs[segment + 1]) segment += 1;

    if (x <= xs[0]) {
      lut[i] = clamp01(ys[0]);
      continue;
    }
    if (x >= xs[n - 1]) {
      lut[i] = clamp01(ys[n - 1]);
      continue;
    }

    const h = xs[segment + 1] - xs[segment];
    if (h <= 1e-9) {
      lut[i] = clamp01(ys[segment]);
      continue;
    }
    const t = (x - xs[segment]) / h;
    const t2 = t * t;
    const t3 = t2 * t;

    // Hermite basis.
    const h00 = 2 * t3 - 3 * t2 + 1;
    const h10 = t3 - 2 * t2 + t;
    const h01 = -2 * t3 + 3 * t2;
    const h11 = t3 - t2;

    lut[i] = clamp01(
      h00 * ys[segment] + h10 * h * tangents[segment] + h01 * ys[segment + 1] + h11 * h * tangents[segment + 1],
    );
  }

  return lut;
}

/** Evaluates the curve at a single point, for UI readouts. */
export function evaluateCurve(points: CurvePoint[], x: number): number {
  const lut = buildCurveLut(points, 256);
  const position = clamp01(x) * (lut.length - 1);
  const low = Math.floor(position);
  const high = Math.min(lut.length - 1, low + 1);
  const t = position - low;
  return lut[low] * (1 - t) + lut[high] * t;
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

/**
 * Describes a curve in words, for the plan review list and for the prompt.
 * Photographers talk about the toe, the mid-tones and the shoulder; the model
 * and the UI should use the same vocabulary.
 */
export function describeCurve(points: CurvePoint[]): string {
  const lut = buildCurveLut(points, 32);
  const identity = (i: number) => i / 31;
  const at = (fraction: number) => lut[Math.round(fraction * 31)];

  const toe = at(0.15) - identity(Math.round(0.15 * 31));
  const mid = at(0.5) - identity(Math.round(0.5 * 31));
  const shoulder = at(0.85) - identity(Math.round(0.85 * 31));

  const parts: string[] = [];
  const term = (delta: number, up: string, down: string) => {
    if (Math.abs(delta) < 0.012) return null;
    const strength = Math.abs(delta) > 0.06 ? 'strong' : 'gentle';
    return `${strength} ${delta > 0 ? up : down}`;
  };

  const toeTerm = term(toe, 'shadow lift', 'shadow crush');
  const midTerm = term(mid, 'mid-tone lift', 'mid-tone drop');
  const shoulderTerm = term(shoulder, 'highlight lift', 'highlight rolloff');
  if (toeTerm) parts.push(toeTerm);
  if (midTerm) parts.push(midTerm);
  if (shoulderTerm) parts.push(shoulderTerm);

  // A colon rather than a dash: this string is shown verbatim in the edit list,
  // in the plan review's before/after columns and under the curve editor, and
  // the interface's copy does not use em dashes anywhere.
  if (parts.length === 0) return 'neutral';
  if (toe < -0.012 && shoulder > 0.012) return `S-curve: ${parts.join(', ')}`;
  if (toe > 0.012 && shoulder < -0.012) return `flattened / matte: ${parts.join(', ')}`;
  return parts.join(', ');
}
